/* Visitor analytics through PostHog and InsightFlare. InsightFlare runs in its own Cloudflare
   account, so its free-plan quotas can never use up the Worker requests that checkout depends on.
   Leave VITE_INSIGHTFLARE_SCRIPT unset and it does not load. */
import { posthogClient, startPostHog } from './posthog';
import { checkoutLogger } from './posthog-logger';

const script = import.meta.env.VITE_INSIGHTFLARE_SCRIPT;

type EventData = Record<string, string | number>;
type Tracker = { track(name: string, data?: EventData): void };

const PLAN_KEY = 'rr-checkout-plan';
const waiting: [string, EventData | undefined][] = [];
const tracker = () => (window as { insightflare?: Tracker }).insightflare;

function insightflare(name: string, data?: EventData) {
  const ready = tracker();
  if (ready) ready.track(name, data);
  else if (script) waiting.push([name, data]);
}

export function track(name: string, data?: EventData) {
  posthogClient()?.capture(name, data);
  insightflare(name, data);
}

/* Ties this visitor's history to the buyer, whom the Worker's server-side events also name by email. */
export function identify(email: string) {
  posthogClient()?.identify(email.trim().toLowerCase(), { email: email.trim() });
}

/* Remembers the plan across the Stripe redirect, which carries only the payment's status. */
export function rememberPlan(plan: string) {
  try {
    sessionStorage.setItem(PLAN_KEY, plan);
  } catch {
    // Storage can be blocked; the purchase is still recorded, just without its plan.
  }
}

function recallPlan() {
  try {
    return sessionStorage.getItem(PLAN_KEY) ?? 'unknown';
  } catch {
    return 'unknown';
  }
}

let paid: string | null = null;

/* The intent from the Stripe redirect (pi_ or seti_), kept for the Discord join button after the URL drops it. */
export const paidIntent = () => paid;

export function startAnalytics() {
  /* Stripe returns to /join with the intent and its client secret in the query string. Drop both
     before any tracker records the page, keeping redirect_status for the thank-you view. */
  const params = new URLSearchParams(location.search);
  // A paid-today plan returns a PaymentIntent; a trial returns the SetupIntent that saved the card.
  const intent = params.get('payment_intent') ?? params.get('setup_intent');
  if (intent) {
    paid = intent;
    for (const key of [
      'payment_intent',
      'payment_intent_client_secret',
      'setup_intent',
      'setup_intent_client_secret',
    ])
      params.delete(key);
    const query = params.toString();
    history.replaceState(history.state, '', `${location.pathname}${query ? `?${query}` : ''}`);
  }
  startPostHog();
  // The intent left the URL above, so a reload of the thank-you page cannot count twice.
  if (intent && new URLSearchParams(location.search).get('redirect_status') === 'succeeded') {
    const plan = recallPlan();
    track('purchase', { plan, trial: intent.startsWith('seti_') ? 'yes' : 'no' });
    checkoutLogger.completed(plan);
  }
  if (!script) return;
  const tag = document.createElement('script');
  tag.src = script;
  tag.defer = true;
  tag.onload = () => waiting.splice(0).forEach(([name, data]) => insightflare(name, data));
  document.head.append(tag);
}
